import { differenceInHours } from 'date-fns';
import { Decimal } from 'decimal.js-light';
import type { OptionalExchangeRatesCurrentRequest } from 'src/apis/exchangerate-service/use-exchange-rates-current';
import type {
  AuctionDetail,
  AuctionReport,
  Price,
} from 'src/apis/monolith/types';
import type { OptionalTransportAuctionServiceRootRequest } from 'src/apis/transport-auction/use-transport-auction-service-root';
import { FormatValue } from 'src/components/data-display/format-value';
import type { AuthenticatedMonolithUserContextValue } from 'src/hooks/use-monolith-user';
import {
  isLongTermAuction,
  isShortTermAuction,
} from 'src/pages/reporting/auctions/details/utils';
import type { Pretty } from 'src/utils/utility-types';

export function convertPricesToEURMWh({
  auction,
  exchangeRates,
}: {
  auction: AuctionDetail;
  exchangeRates: OptionalExchangeRatesCurrentRequest;
}) {
  const hours = differenceInHours(
    new Date(auction.runtime.end),
    new Date(auction.runtime.start)
  );

  try {
    const startingPrice = convertPriceToEURMWh({
      price: auction.startingPrice,
      exchangeRates,
      hours,
    });

    const priceTick = isShortTermAuction(auction)
      ? convertPriceToEURMWh({
          price: auction.priceTick,
          exchangeRates,
          hours,
        })
      : null;

    const largePriceStep = isLongTermAuction(auction)
      ? convertPriceToEURMWh({
          price: auction.largePriceStep,
          exchangeRates,
          hours,
        })
      : null;

    const smallPriceStep = isLongTermAuction(auction)
      ? convertPriceToEURMWh({
          price: auction.smallPriceStep,
          exchangeRates,
          hours,
        })
      : null;

    const fees0 = {
      regulatedTariff: auction.regulatedTariff0
        ? convertPriceToEURMWh({
            price: auction.regulatedTariff0,
            exchangeRates,
            hours,
          })
        : undefined,
      operatingCharge: auction.operatingCharge0
        ? convertPriceToEURMWh({
            price: auction.operatingCharge0,
            exchangeRates,
            hours,
          })
        : undefined,
      accountingCharge: auction.accountingCharge0
        ? convertPriceToEURMWh({
            price: auction.accountingCharge0,
            exchangeRates,
            hours,
          })
        : undefined,
      gasQualityConversionCharge: auction.gasQualityConversionCharge0
        ? convertPriceToEURMWh({
            price: auction.gasQualityConversionCharge0,
            exchangeRates,
            hours,
          })
        : undefined,
      measurementCharge: auction.measurementCharge0
        ? convertPriceToEURMWh({
            price: auction.measurementCharge0,
            exchangeRates,
            hours,
          })
        : undefined,
      biogasCharge: auction.biogasCharge0
        ? convertPriceToEURMWh({
            price: auction.biogasCharge0,
            exchangeRates,
            hours,
          })
        : undefined,
      balancingCharge: auction.balancingCharge0
        ? convertPriceToEURMWh({
            price: auction.balancingCharge0,
            exchangeRates,
            hours,
          })
        : undefined,
      commodityFee: auction.commodityFee0
        ? convertPriceToEURMWh({
            price: auction.commodityFee0,
            exchangeRates,
            hours,
          })
        : undefined,
    };

    const fees1 = {
      regulatedTariff: auction.regulatedTariff1
        ? convertPriceToEURMWh({
            price: auction.regulatedTariff1,
            exchangeRates,
            hours,
          })
        : undefined,
      operatingCharge: auction.operatingCharge1
        ? convertPriceToEURMWh({
            price: auction.operatingCharge1,
            exchangeRates,
            hours,
          })
        : undefined,
      accountingCharge: auction.accountingCharge1
        ? convertPriceToEURMWh({
            price: auction.accountingCharge1,
            exchangeRates,
            hours,
          })
        : undefined,
      gasQualityConversionCharge: auction.gasQualityConversionCharge1
        ? convertPriceToEURMWh({
            price: auction.gasQualityConversionCharge1,
            exchangeRates,
            hours,
          })
        : undefined,
      measurementCharge: auction.measurementCharge1
        ? convertPriceToEURMWh({
            price: auction.measurementCharge1,
            exchangeRates,
            hours,
          })
        : undefined,
      biogasCharge: auction.biogasCharge1
        ? convertPriceToEURMWh({
            price: auction.biogasCharge1,
            exchangeRates,
            hours,
          })
        : undefined,
      balancingCharge: auction.balancingCharge1
        ? convertPriceToEURMWh({
            price: auction.balancingCharge1,
            exchangeRates,
            hours,
          })
        : undefined,
      commodityFee: auction.commodityFee1
        ? convertPriceToEURMWh({
            price: auction.commodityFee1,
            exchangeRates,
            hours,
          })
        : undefined,
    };

    return {
      startingPrice,
      priceTick,
      largePriceStep,
      smallPriceStep,
      fees0,
      fees1,
    };
  } catch (error) {
    // we could not convert one of the prices, therefor we disable the option to change to EUR/MWh
    return null;
  }
}

export type PricesInEURMWh = ReturnType<typeof convertPricesToEURMWh>;
export type FeesInEURMWh = Pretty<
  Exclude<PricesInEURMWh, null>['fees0'] &
    Exclude<PricesInEURMWh, null>['fees1']
>;

export function convertPriceToEURMWhValue({
  price: priceOrPrices,
  exchangeRates,
  hours,
}: {
  price: Price | [Price] | [Price, Price];
  exchangeRates: OptionalExchangeRatesCurrentRequest;
  hours: number;
}) {
  const price = Array.isArray(priceOrPrices)
    ? preferPriceInEUR(priceOrPrices)
    : priceOrPrices;

  if (
    price.originalUnit.physicalUnit !== 'K_WH_D' &&
    price.originalUnit.physicalUnit !== 'K_WH_H'
  ) {
    return null; // keep original value if physical unit is not supported
  }
  const exchangeRateFactor =
    price.originalUnit.currency === 'EUR'
      ? 1
      : exchangeRates.response?.data.find(
          (rate) =>
            rate.fromCurrency === 'EUR' &&
            rate.toCurrency === price.originalUnit.currency
        )?.rate;

  if (exchangeRateFactor === undefined) {
    throw new Error('Exchange rate factor is undefined and needed.');
  }

  const fullUnitFactor = price.originalUnit.currencySubunit ? 100 : 1;
  const kwhToMwhFactor = 1000;
  const perDayToHourFactor =
    price.originalUnit.physicalUnit === 'K_WH_D' ? 24 : 1;

  const value = new Decimal(price.originalValue.value)
    .mul(kwhToMwhFactor)
    .div(exchangeRateFactor)
    .div(fullUnitFactor)
    .mul(perDayToHourFactor)
    .div(hours)
    .toNumber();

  return value;
}

function convertPriceToEURMWh(props: {
  price: Price | [Price] | [Price, Price];
  exchangeRates: OptionalExchangeRatesCurrentRequest;
  hours: number;
}) {
  const value = convertPriceToEURMWhValue(props);
  if (value === null) return null; // keep original value
  return <FormatValue value={value} label="EUR/MWh" type="exact-decimals" />;
}

// some prices support two currencies and in these cases
// we prefer EUR over other currencies as we can ignore
// the exchange rate factor
function preferPriceInEUR(price: [Price] | [Price, Price]) {
  return (
    price.find((price) => price.originalUnit.currency === 'EUR') ?? price[0]
  );
}

export function showManagePriceSteps({
  auction,
  monolithUser,
  transportAuctionServiceRoot,
}: {
  auction: AuctionDetail | AuctionReport;
  monolithUser: AuthenticatedMonolithUserContextValue;
  transportAuctionServiceRoot: OptionalTransportAuctionServiceRootRequest;
}) {
  const owningTso =
    monolithUser?.isOperator &&
    (auction.networkPoint.tso0Id === monolithUser.referenceId ||
      (auction.networkPoint.direction === 'BUNDLE' &&
        auction.networkPoint.tso1Id === monolithUser.referenceId));

  const auctionWindow =
    'auctionWindow' in auction ? auction.auctionWindow : auction.round;
  // auction window "should" always be defined, but monolith types are a bit tricky.
  // to be on the safe side we assume that we're not in a small price step, when it is undefined.
  const isSmallPriceStep = auctionWindow
    ? auctionWindow.split('.')[1] !== '0'
    : false;

  return (
    owningTso &&
    !isSmallPriceStep &&
    auction.algorithmType === 'LONG_TERM' &&
    (auction.phase === 'OPEN' || auction.phase === 'CLOSED') &&
    auction.uuid && // monolith rolled out to support price step changes
    transportAuctionServiceRoot?.response?.data._links.priceStepsChangeRequest
  );
}
